import { routing } from '@/i18n/routing';
import { buildPathWithLocale } from '@/lib/i18n/locale-path';
import { isLocalAppEnv } from '@/lib/runtime/app-env';
import { resolveWorkspaceStaticAsset } from '@/workspace/static-assets';
import type { Locale } from 'next-intl';
import { resolveStoragePublicUrl } from './resolve-storage-public-url';
import { resolveSafeSameOriginCallbackUrl } from './safe-callback-url';

function getTrimmedProcessEnvValue(name: string): string | undefined {
  const value = process.env[name]?.trim();
  return value ? value : undefined;
}

/**
 * Get the base URL of the application
 * - Browser: use current origin to avoid port / domain mismatch.
 * - Server: fall back to env / default.
 */
export function getBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return (
    getTrimmedProcessEnvValue('NEXT_PUBLIC_BASE_URL') ??
    (isLocalAppEnv()
      ? `http://localhost:${getTrimmedProcessEnvValue('PORT') ?? 3000}`
      : '')
  );
}

/**
 * Check if the locale should be appended to the URL
 */
function shouldAppendLocale(locale?: Locale | null): locale is Locale {
  return !!locale && locale !== routing.defaultLocale && locale !== 'default';
}

/**
 * Get the localized path without adding any host.
 */
export function getPathWithLocale(url: string, locale?: Locale | null): string {
  return shouldAppendLocale(locale) ? buildPathWithLocale(url, locale) : url;
}

/**
 * Get the URL of the application with the locale appended
 */
export function getUrlWithLocale(url: string, locale?: Locale | null): string {
  const baseUrl = getBaseUrl();
  return `${baseUrl}${getPathWithLocale(url, locale)}`;
}

/**
 * Adds locale to the callbackURL parameter in authentication URLs
 *
 * Example:
 * Input: http://localhost:3000/api/auth/reset-password/token?callbackURL=/auth/reset-password
 * Output: http://localhost:3000/api/auth/reset-password/token?callbackURL=/zh/auth/reset-password
 *
 * Input: http://localhost:3000/api/auth/verify-email?token=eyJhbGciOiJIUzI1NiJ9&callbackURL=/dashboard
 * Output: http://localhost:3000/api/auth/verify-email?token=eyJhbGciOiJIUzI1NiJ9&callbackURL=/zh/dashboard
 *
 * @param url - The original URL with callbackURL parameter
 * @param locale - The locale to add to the callbackURL
 * @returns The URL with locale added to callbackURL if necessary
 */
function getUrlWithLocaleInCallbackUrl(url: string, locale: Locale): string {
  // If we shouldn't append locale, return original URL
  if (!shouldAppendLocale(locale)) {
    return url;
  }

  try {
    // Parse the URL
    const urlObj = new URL(url);

    // Check if there's a callbackURL parameter
    const callbackURL = urlObj.searchParams.get('callbackURL');

    if (callbackURL) {
      // Only modify the callbackURL if it doesn't already include the locale
      if (!callbackURL.match(new RegExp(`^/${locale}(/|$)`))) {
        // Add locale to the callbackURL
        const localizedCallbackURL = callbackURL.startsWith('/')
          ? `/${locale}${callbackURL}`
          : `/${locale}/${callbackURL}`;

        // Update the search parameter
        urlObj.searchParams.set('callbackURL', localizedCallbackURL);
      }
    }

    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL
    return url;
  }
}

/**
 * Sanitize auth callback URL to prevent open redirect.
 * - Allow relative paths starting with "/"
 * - Allow absolute URLs only if same-origin as base URL
 */
export function getSafeAuthCallbackUrl(
  value: string | null | undefined,
  fallback: string
): string {
  return (
    resolveSafeSameOriginCallbackUrl({
      value,
      baseUrl: getBaseUrl(),
    }) ?? fallback
  );
}

/**
 * Get the URL of the image, if the image is a relative path, it will be prefixed with the base URL
 * @param image - The image URL
 * @returns The URL of the image
 */
export function getImageUrl(image: string): string {
  const baseUrl = getBaseUrl();

  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  if (image.startsWith('/')) {
    return `${baseUrl}${image}`;
  }
  return `${baseUrl}/${image}`;
}

/**
 * Get the public storage URL (e.g., R2 public domain) for a given path
 * - If already absolute, return as-is.
 * - If STORAGE_PUBLIC_URL is not set, fall back to the original path.
 */
export function getStoragePublicUrl(path: string): string {
  if (!path) return path;
  const localAsset = resolveWorkspaceStaticAsset(path);
  if (localAsset.startsWith('/workspace/')) return localAsset;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = (getTrimmedProcessEnvValue('STORAGE_PUBLIC_URL') ?? '').replace(
    /\/$/,
    ''
  );
  return resolveStoragePublicUrl(path, base);
}
